/**
 * upGrad internship catalogue API (courses + their categories).
 *
 * Called from the browser, as the source project does — no credential is
 * involved, but the calling origin must be on the service's CORS allowlist.
 *
 * The host comes from NEXT_PUBLIC_INTERNSHIP_PLATFORM_SERVICE via `APP_URL`
 * (src/config/urls.ts), following the same convention as every other base URL
 * in this app. There is deliberately no hard-coded fallback: an unset variable
 * fails loudly in the console rather than silently pointing at production.
 *
 * Verified against the live service:
 *   POST /api/search              { type, categoryId, searchTerm, skip, limit }
 *   GET  /api/categories?type=…
 */

import { APP_URL } from "@/config/urls";

/**
 * Base URL for every internship service call — the catalogue here and the
 * enrolment/payment calls in `internshipPayment`, which share one host.
 *
 * Returns "" when the env var is unset so callers short-circuit; `context` names
 * the caller in the console so it is obvious what stopped working.
 */
export const resolveInternshipApiBaseUrl = (context: string): string => {
  const configured = APP_URL.INTERNSHIP_PLATFORM_SERVICE?.trim();
  if (configured) return configured.replace(/\/+$/, "");

  console.error(
    `[${context}] NEXT_PUBLIC_INTERNSHIP_PLATFORM_SERVICE is not set — the internship service is unreachable.`
  );
  return "";
};

/** "courses" today; the service also serves "projects". */
export type InternshipSearchType = "courses" | "projects";

export interface IInternshipCategory {
  id: string;
  type: string;
}

export interface IInternshipCourse {
  course_id: string;
  course_name: string;
  category?: { category_id?: string; type?: string } | null;
  brochure_url?: string;
  fee_amount?: number;
  tag?: string;
  image_url?: string;
  mode?: string;
  duration?: string;
  cohort_id?: number;
}

export interface IInternshipSearchResult {
  data: IInternshipCourse[];
  total: number;
}

export interface IInternshipSearchParams {
  type: InternshipSearchType;
  categoryId: string;
  searchTerm: string;
  /**
   * PAGE INDEX, not a row offset — verified against the service: `skip: 1` with
   * `limit: 2` returns the 3rd and 4th rows. Pass `pageNo + 1` to advance.
   */
  skip: number;
  limit: number;
}

/** Categories for the chip row. Returns [] on any failure — the UI degrades to "All". */
export async function getInternshipCategories(
  type: InternshipSearchType,
  signal?: AbortSignal
): Promise<IInternshipCategory[]> {
  const baseUrl = resolveInternshipApiBaseUrl("internshipData");
  if (!baseUrl) return [];

  try {
    const res = await fetch(
      `${baseUrl}/api/categories?type=${encodeURIComponent(type)}`,
      { headers: { accept: "application/json" }, signal }
    );
    if (!res.ok) return [];

    const body = await res.json();
    const categories = body?.categories;
    return Array.isArray(categories) ? categories : [];
  } catch {
    // AbortError included — the caller replaces the result anyway.
    return [];
  }
}

/** One page of courses. Returns an empty page on any failure. */
export async function searchInternshipCatalogue(
  params: IInternshipSearchParams,
  signal?: AbortSignal
): Promise<IInternshipSearchResult> {
  const empty: IInternshipSearchResult = { data: [], total: 0 };

  const baseUrl = resolveInternshipApiBaseUrl("internshipData");
  if (!baseUrl) return empty;

  try {
    const res = await fetch(`${baseUrl}/api/search`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(params),
      signal,
    });
    // The service answers 201 for this POST, not 200.
    if (!res.ok) return empty;

    const body = await res.json();
    return {
      data: Array.isArray(body?.data) ? body.data : [],
      total: Number(body?.total) || 0,
    };
  } catch {
    return empty;
  }
}
